import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Copy, Check, Save, Clock, KeyRound, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact panel rendered under each event card on the organizer/admin
 * dashboards. Fetches the auto-created "Internal guest list" for this event,
 * exposes an editable name + close-time (HH:MM, morning after the event),
 * and shows the shareable RSVP link.
 *
 * Access is enforced server-side — only admins or users with `is_internal=True`
 * see any data here (a 403 collapses the panel with a soft note).
 */
export const InternalListPanel = ({ eventId, eventTitle, token, user }) => {
  const canAccess = !!(user?.is_admin || user?.is_internal);

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(canAccess);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [closeTime, setCloseTime] = useState('01:30');

  const fetchList = useCallback(async () => {
    if (!canAccess) return;
    try {
      const res = await axios.get(`${API}/guest-lists?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const internal = (res.data || []).find((l) => l.is_internal);
      if (internal) {
        setList(internal);
        setName(internal.name || 'Internal guest list');
        setCloseTime(internal.close_time || '01:30');
      }
    } catch {
      // 403 for standard promoters is expected; other failures fall through silently
    } finally {
      setLoading(false);
    }
  }, [eventId, token, canAccess]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const save = async () => {
    if (!list) return;
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      toast.error('Name cannot be empty');
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(closeTime)) {
      toast.error('Close time must be HH:MM (24-hr)');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/guest-lists/${list.id}`,
        { name: trimmedName, close_time: closeTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setList(res.data);
      toast.success('Internal list updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const copyRsvp = async () => {
    if (!list?.code) return;
    const url = `${window.location.origin}/rsvp/${list.code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Internal RSVP link copied');
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Copy failed — long-press to select instead');
    }
  };

  if (!canAccess) return null;

  if (loading) {
    return (
      <div className="border border-blue-500/20 bg-blue-500/5 p-3 mt-3 flex items-center gap-2" data-testid={`internal-panel-loading-${eventId}`}>
        <Loader2 size={12} className="animate-spin text-blue-300" />
        <span className="font-mono text-[10px] text-zinc-500 tracking-widest">LOADING INTERNAL LIST…</span>
      </div>
    );
  }

  if (!list) {
    // Panel isn't visible when there's no internal list — should never happen
    // in practice because startup backfill + create hook always seed one.
    return null;
  }

  const rsvpUrl = `${window.location.origin}/rsvp/${list.code}`;
  const dirty = name.trim() !== (list.name || '') || closeTime !== (list.close_time || '01:30');

  return (
    <div
      className="border border-blue-500/30 bg-blue-500/[0.04] p-3 mt-3 space-y-3"
      data-testid={`internal-panel-${eventId}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KeyRound size={12} className="text-blue-300" />
          <span className="font-mono text-[10px] tracking-widest text-blue-300">INTERNAL GUEST LIST</span>
        </div>
        <span className="font-mono text-[10px] text-zinc-500">
          {list.guest_count} added
        </span>
      </div>

      {/* Editable name */}
      <div>
        <label className="font-mono text-[10px] text-zinc-500 tracking-widest">LIST NAME</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="w-full h-8 mt-1 bg-[#050505] border border-zinc-800 text-white text-sm px-2 focus:border-blue-400/60 focus:outline-none"
          data-testid={`internal-name-${eventId}`}
        />
      </div>

      {/* Editable close time */}
      <div>
        <label className="font-mono text-[10px] text-zinc-500 tracking-widest flex items-center gap-1">
          <Clock size={10} /> CLOSE TIME (MORNING AFTER · IST)
        </label>
        <input
          type="time"
          value={closeTime}
          onChange={(e) => setCloseTime(e.target.value)}
          className="w-32 h-8 mt-1 bg-[#050505] border border-zinc-800 text-white text-sm px-2 focus:border-blue-400/60 focus:outline-none"
          data-testid={`internal-close-time-${eventId}`}
        />
        <span className="ml-2 font-mono text-[10px] text-zinc-500">
          Default 01:30 · standard promoter lists still close at 7 PM
        </span>
      </div>

      {/* RSVP link */}
      <div className="bg-[#050505] border border-zinc-800 px-2 py-1.5 flex items-center gap-2">
        <code className="text-blue-300 font-mono text-[11px] flex-1 break-all">{rsvpUrl}</code>
        <button
          onClick={copyRsvp}
          className="text-zinc-400 hover:text-blue-300 p-1"
          title="Copy internal RSVP link"
          data-testid={`internal-copy-link-${eventId}`}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
        <a
          href={rsvpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-400 hover:text-blue-300 p-1"
          title="Open in new tab"
          data-testid={`internal-open-link-${eventId}`}
        >
          <ExternalLink size={12} />
        </a>
      </div>

      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="w-full h-8 border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-xs font-mono tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`internal-save-${eventId}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          SAVE CHANGES
        </button>
      )}
    </div>
  );
};

export default InternalListPanel;
